<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import {
  EbomPartAllocationSuggestionDecision,
  EbomPartSnoozeStatus,
  EbomPartTreeEbomPartsQuery,
  EbomPartTreeEbomPartsQueryVariables,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import { computed, ref } from 'vue'
import EbomPartSnoozeFilters from '@/components/ebom/EbomPartSnoozeFilters.vue'
import ArticleQuantity from '@/components/article/ArticleQuantity.vue'
import useTree, { TreeItemNode } from '@/composables/useTree'
import TreeView from '@/components/tree/TreeView.vue'

const props = defineProps<{
  ebomId: string
}>()
const selectedEbomPartId = defineModel<string | undefined>()

const { t } = useI18n()
const { treeRootNodes } = useTree()

export type EbomPartTreeEbomPart = EbomPartTreeEbomPartsQuery['article']['ebom']['ebomParts'][0] & {
  netAllocationMilliQuantity: number
  undecidedSuggestionsCount: number
}
const fetchQuery = useQuery<EbomPartTreeEbomPartsQuery, EbomPartTreeEbomPartsQueryVariables>(
  gql`
    query EbomPartTreeEbomParts($id: ID!) {
      article {
        ebom(id: $id) {
          ebomParts {
            id
            parentId
            articleNumber
            name
            revision
            milliQuantity
            unit
            snoozeStatus
            article {
              id
            }
            articlePath {
              articleNumbers
            }
            stepAllocations {
              milliQuantity
            }
            stepAllocationSuggestions {
              decision
            }
          }
        }
      }
    }
  `,
  () => ({
    id: props.ebomId,
  }),
)

type EbomPartTreeNode = TreeItemNode<EbomPartTreeEbomPart> & {
  children: EbomPartTreeNode[]
  hasAllocationAbberation: boolean
  hasOpenSuggestions: boolean
  hasSnoozeStatuses: EbomPartSnoozeStatus[]
}

const nodeProcessor = (node: TreeItemNode<EbomPartTreeEbomPart>): EbomPartTreeNode => {
  const children = node.children.map(nodeProcessor)
  const hasAllocationAbberation =
    node.item.netAllocationMilliQuantity != 0 || children.some((c) => c.hasAllocationAbberation)
  const hasOpenSuggestions =
    node.item.stepAllocationSuggestions.filter(
      (s) => s.decision == EbomPartAllocationSuggestionDecision.Undecided,
    ).length > 0 || children.some((c) => c.hasOpenSuggestions)
  const hasSnoozeStatuses = children.reduce(
    (acc, c) => {
      if (!acc.includes(c.item.snoozeStatus)) {
        acc.push(c.item.snoozeStatus)
      }
      return acc
    },
    [node.item.snoozeStatus] as EbomPartSnoozeStatus[],
  )

  return {
    ...node,
    children,
    hasAllocationAbberation,
    hasOpenSuggestions,
    hasSnoozeStatuses,
  }
}
const rootNodes = computed(() => {
  const raw = fetchQuery.result.value?.article.ebom.ebomParts || []
  const processed = raw
    .map((p) => {
      const netAllocationMilliQuantity = p.article
        ? p.stepAllocations.reduce((agg, c) => agg + c.milliQuantity, 0) - p.milliQuantity
        : 0
      const undecidedSuggestionsCount = p.stepAllocationSuggestions.filter(
        (s) => s.decision == EbomPartAllocationSuggestionDecision.Undecided,
      ).length
      return {
        ...p,
        netAllocationMilliQuantity,
        undecidedSuggestionsCount,
      }
    })
    .toSorted((a, b) => a.articleNumber.localeCompare(b.articleNumber))
  return treeRootNodes(processed).map(nodeProcessor)
})

const showOnlySnoozedStatuses = ref<EbomPartSnoozeStatus[]>([EbomPartSnoozeStatus.None])
const showOnlyOpenSuggestions = ref(false)
const showOnlyAllocationDisparity = ref(false)
const filterProcessor = (nodes: EbomPartTreeNode[]): EbomPartTreeNode[] =>
  nodes
    .filter((n) => {
      if (!n.hasSnoozeStatuses.some((s) => showOnlySnoozedStatuses.value.includes(s))) {
        console.log('No right snooze status', n.hasSnoozeStatuses)
        return false
      }
      if (showOnlyOpenSuggestions.value && !n.hasOpenSuggestions) {
        console.log('No open suggestions')
        return false
      }
      if (showOnlyAllocationDisparity.value && !n.hasAllocationAbberation) {
        console.log('No allocation abberations')
        return false
      }

      return true
    })
    .map((n) => ({
      ...n,
      children: filterProcessor(n.children),
    }))
const filteredNodes = computed(() => filterProcessor(rootNodes.value))
</script>

<template>
  <v-row>
    <v-col cols="6">
      <h2>
        {{ t('component.ebomPartTree.title') }}
        <v-progress-circular v-if="fetchQuery.loading.value" indeterminate size="20" />
      </h2>
    </v-col>
    <v-col class="mb-3 text-right">
      <ebom-part-snooze-filters v-model="showOnlySnoozedStatuses">
        <template #activator="{ props }">
          <v-btn
            density="compact"
            icon="alarm_off"
            :title="t('component.ebomPartTree.filterSnoozedHint')"
            :variant="showOnlySnoozedStatuses.length == 0 ? 'flat' : 'outlined'"
            v-bind="props"
          />
        </template>
      </ebom-part-snooze-filters>
      <v-btn
        density="compact"
        class="ml-1"
        :variant="showOnlyOpenSuggestions ? 'outlined' : 'plain'"
        icon="settings_suggest"
        :title="t('component.ebomPartTree.filterOpenSuggestionsHint')"
        @click="showOnlyOpenSuggestions = !showOnlyOpenSuggestions"
      />
      <v-btn
        density="compact"
        :variant="showOnlyAllocationDisparity ? 'outlined' : 'plain'"
        class="ml-1"
        icon="difference"
        :title="t('component.ebomPartTree.filterAllocationDisparityHint')"
        @click="showOnlyAllocationDisparity = !showOnlyAllocationDisparity"
      />
    </v-col>
  </v-row>
  <div style="height: 76vh; overflow-y: scroll; overflow-x: hidden; user-select: none">
    <tree-view v-model:activated="selectedEbomPartId" :nodes="filteredNodes">
      <template #prepend="{ item, isOpen }">
        <v-icon
          density="compact"
          :icon="item.children.length > 0 ? (isOpen ? 'folder_open' : 'folder') : 'settings'"
          :color="item.children.length && item.hasAllocationAbberation ? 'error' : undefined"
        />
      </template>
      <template #title="{ item }">
        <div class="title" :title="item.item.name">
          {{ item.item.articleNumber }}/{{ item.item.revision }}
        </div>
      </template>
      <template #subtitle="{ item }">
        <div class="subtitle" :title="item.item.name">
          {{ item.item.name }}
        </div>
      </template>
      <template #append="{ item }">
        <v-chip v-if="item.item.undecidedSuggestionsCount > 0" density="compact" class="count">
          {{ item.item.undecidedSuggestionsCount }}
        </v-chip>
        <v-chip
          v-if="!!item.item.article"
          :color="
            item.item.netAllocationMilliQuantity == 0
              ? 'success'
              : item.item.snoozeStatus == EbomPartSnoozeStatus.None
                ? 'error'
                : 'warning'
          "
          density="compact"
          class="count"
        >
          <v-icon v-if="item.item.snoozeStatus != EbomPartSnoozeStatus.None">alarm_off</v-icon>
          <article-quantity
            :milli-quantity="item.item.netAllocationMilliQuantity"
            :unit="item.item.unit"
            unit-abbreviated
          />
        </v-chip>
      </template>
    </tree-view>
  </div>
</template>

<style scoped lang="scss">
.title,
.subtitle {
  font-size: 0.8em;
}
.title {
  font-weight: bold;
}
</style>
